// Gespräche: History, Live Calls, Telefonie-Qualität (Runde 7, Agent 7D): Typen und Client nach crm/API.md, Abschnitt 22.4.
import { http } from "./client";
import type { AufnahmeArt } from "./aufnahmen";

export interface GespraechAufnahme {
  verfuegbar: boolean;
  art: AufnahmeArt | null;
  laeuft_ab_am: string | null;
  /** Länge der Aufnahme bzw. Mailbox-Nachricht in Sekunden */
  dauer_s?: number | null;
  /** Warum nicht abspielbar: `abgelaufen` oder `keine_berechtigung` */
  grund?: "abgelaufen" | "keine_berechtigung" | null;
}

export type QualitaetBewertung = "gut" | "mittel" | "schlecht";

export interface Gespraech {
  /** ID des Anrufs: Anruf-Aktivität (`acti_…`) bzw. CRM-Anruf (`call_crm_…`); Eingabe für den Aufnahme-Player */
  call_id: string;
  activity_id: string | null;
  /** CRM-Anruf (`call_crm_…`), falls der Anruf über die CRM-Telefonie lief */
  crm_call_id?: string | null;
  lead_id: string | null;
  lead_name: string | null;
  kontakt_id?: string | null;
  kontakt_name: string | null;
  nummer: string | null;
  nummer_formatiert?: string | null;
  user_id: string | null;
  user_name: string | null;
  richtung: "eingehend" | "ausgehend";
  /** Ergebnis wie Close: answered, no-answer, vm-left … */
  ergebnis: string | null;
  dauer_s: number | null;
  zeit: string;
  /** „Anruf an Max Muster“, „Verpasster Anruf von …“ */
  titel?: string;
  notiz: string | null;
  notiz_html?: string | null;
  aufnahme: GespraechAufnahme;
  quelle?: "close" | "crm";
  /** Kurzbewertung der Leitung (nur bei Anrufen der CRM-Telefonie mit Messung) */
  qualitaet?: { mos: number | null; bewertung: QualitaetBewertung } | null;
}

export interface LiveGespraech {
  call_id: string;
  crm_call_id?: string | null;
  lead_id: string | null;
  lead_name: string | null;
  kontakt_name?: string | null;
  nummer?: string | null;
  nummer_formatiert?: string | null;
  user_id: string | null;
  user_name: string | null;
  richtung: "eingehend" | "ausgehend";
  /** initiated · ringing · in-progress */
  status: string;
  status_text?: string;
  /** Beginn des Gesprächs (angenommen) bzw. des Anrufs */
  seit: string;
  gestartet?: string;
  angenommen?: boolean;
  quelle?: "crm" | "close";
}

export interface MeetingHeute {
  id: string;
  titel: string;
  beginn: string | null;
  ende: string | null;
  lead_id: string | null;
  lead_name: string | null;
  user_id: string | null;
  user_name: string | null;
  teilnehmer: number;
}

export interface QualitaetZeile {
  call_id: string;
  crm_call_id?: string | null;
  user_id: string | null;
  user_name: string | null;
  zeit: string;
  dauer_s: number | null;
  richtung?: "eingehend" | "ausgehend";
  lead_id?: string | null;
  mos: number | null;
  jitter_ms: number | null;
  rtt_ms: number | null;
  paketverlust_prozent: number | null;
  bewertung: QualitaetBewertung | null;
  warnungen: string[];
}

export interface QualitaetNutzer {
  user_id: string;
  name: string | null;
  anrufe: number;
  mos_durchschnitt: number | null;
  gut?: number;
  mittel?: number;
  schlecht?: number;
  paketverlust_durchschnitt?: number | null;
  wiederverbindungen?: number;
}

export interface QualitaetZusammenfassung {
  anrufe: number;
  mos_durchschnitt: number | null;
  schlecht: number;
  je_nutzer: QualitaetNutzer[];
  gut?: number;
  mittel?: number;
  /** alle Anrufe der CRM-Telefonie im Zeitraum, auch ohne Messung */
  gesamt?: number;
  ohne_messung?: number;
  fehlgeschlagen?: number;
  anteil_schlecht_prozent?: number;
  paketverlust_durchschnitt?: number | null;
  jitter_durchschnitt?: number | null;
  rtt_durchschnitt?: number | null;
}

export type StoerungArt = "fehler" | "qualitaet" | "mikrofon" | "verbindung";

export interface Stoerung {
  call_id: string;
  zeit: string;
  user_id: string | null;
  user_name: string | null;
  art: StoerungArt;
  schwere: "hoch" | "mittel";
  code: string | null;
  text: string;
}

export interface Alarm {
  call_id: string;
  zeit: string;
  text: string;
  user_id?: string | null;
  /** Störungsart oder `haeufung` (mehrere schlechte Anrufe eines Nutzers) */
  art?: string;
}

export interface QualitaetTag {
  tag: string;
  anrufe: number;
  mos_durchschnitt: number | null;
  schlecht: number;
}

export interface Geraetetest {
  user_id: string;
  user_name: string | null;
  zeit: string;
  ok: boolean;
  bewertung: string | null;
  mos: number | null;
  rtt_ms: number | null;
  jitter_ms: number | null;
  edge: string | null;
}

export interface QualitaetAntwort {
  data: QualitaetZeile[];
  zusammenfassung: QualitaetZusammenfassung | null;
  alarme: Alarm[];
  stoerungen?: Stoerung[];
  verlauf?: QualitaetTag[];
  geraetetests?: Geraetetest[];
  von?: string;
  bis?: string;
}

export interface QualitaetProbe {
  zeit: string;
  mos: number | null;
  jitter_ms: number | null;
  rtt_ms: number | null;
  paketverlust_prozent: number | null;
  warnungen: string[];
}

/** Qualität eines einzelnen Anrufs (Antwort von `GET /api/gespraeche/{call_id}`) mit Sekundenproben und Zeitleiste der Warnungen */
export interface QualitaetEinzel extends QualitaetZeile {
  edge?: string | null;
  codec?: string | null;
  eingang?: string | null;
  app?: string | null;
  wiederverbindungen?: number;
  mos_min?: number | null;
  fehler?: string | null;
  zeitleiste?: { zeit: string | null; name: string; text: string; aktiv: boolean }[];
  proben?: QualitaetProbe[];
}

export interface GespraecheFilter {
  user_id?: string;
  lead_id?: string;
  q?: string;
  /** `alle` (Standard): jeder Begriff muss passen; `irgendein`: ein Begriff genügt */
  match?: "alle" | "irgendein";
  min_dauer_s?: number;
  max_dauer_s?: number;
  nur_aufgenommen?: boolean;
  richtung?: "eingehend" | "ausgehend";
  /** Anruf-Ergebnisse, kommagetrennt */
  ergebnis?: string;
  von?: string;
  bis?: string;
  /** auch Anrufe ohne Dauer, Notiz und Aufnahme */
  alle?: boolean;
  /** `-zeit` (Standard) · `zeit` · `-dauer` · `dauer` */
  sort?: string;
  cursor?: string | null;
  limit?: number;
}

export const gespraecheApi = {
  history: (f: GespraecheFilter = {}, signal?: AbortSignal) =>
    http.get<{ data: Gespraech[]; has_more: boolean; cursor: string | null; total: number }>("/api/gespraeche", { ...f }, signal),
  live: (signal?: AbortSignal) => http.get<{ data: LiveGespraech[]; meetings_heute?: MeetingHeute[] }>("/api/gespraeche/live", undefined, signal),
  qualitaet: (p: { von?: string; bis?: string; user_id?: string } = {}, signal?: AbortSignal) =>
    http.get<QualitaetAntwort>("/api/gespraeche/qualitaet", p, signal),
  /** Aufnahme-Hinweise für bis zu 100 Anruf-Aktivitäten auf einmal (Spalte „Aufnahme“ der Anrufliste) */
  aufnahmen: (ids: string[], signal?: AbortSignal) =>
    http.get<{ data: Record<string, GespraechAufnahme> }>("/api/gespraeche/aufnahmen", { ids: ids.join(",") }, signal),
  eins: (callId: string, signal?: AbortSignal) =>
    http.get<{ gespraech: Gespraech; qualitaet: QualitaetEinzel | null }>(`/api/gespraeche/${encodeURIComponent(callId)}`, undefined, signal),
};
